import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import {
  Copy06,
  Dataflow03,
  FilePlus02,
  Folder,
  Home03,
  LayersThree01,
  Play,
  Settings01,
  UserPlus01,
  Zap,
} from '@untitled-ui/icons-react'
import type { CommandBarItem } from './types'
import { CommandBarKeycap } from './CommandBarKeycap'
import { getCommandBarItemSx } from './commandBar.styles'
import { formatCommandBarActionShortcut } from './utils/formatCommandBarActionShortcut'

type CommandBarItemRowProps = {
  item: CommandBarItem
  selected: boolean
  recentLabel?: string
  onSelect: () => void
  onHover: () => void
  onExecute: () => void
}

function getItemIcon(item: CommandBarItem) {
  if (item.actionId) {
    switch (item.actionId) {
      case 'create-folder':
      case 'view-all-documents':
        return Folder
      case 'add-referential':
      case 'apply-referential-declaration':
        return LayersThree01
      case 'apply-model':
      case 'change-model-declaration':
        return FilePlus02
      case 'duplicate-folder-declaration':
        return Copy06
      case 'add-client':
        return UserPlus01
      case 'create-automation':
      case 'apply-automation':
      case 'run-automation-declaration':
        return Zap
      case 'go-dashboard':
        return Home03
      case 'open-settings':
        return Settings01
      default:
        return Play
    }
  }

  switch (item.type) {
    case 'document':
      return Folder
    case 'client':
      return UserPlus01
    case 'referential':
      return LayersThree01
    case 'flux':
      return Dataflow03
    case 'recent':
    case 'action':
    default:
      return Zap
  }
}

export const CommandBarItemRow = ({
  item,
  selected,
  recentLabel,
  onSelect,
  onHover,
  onExecute,
}: CommandBarItemRowProps) => {
  const Icon = getItemIcon(item)

  return (
    <Box
      component="li"
      role="option"
      aria-selected={selected}
      data-command-bar-item
      sx={getCommandBarItemSx(selected, item.disabled)}
      onMouseEnter={onHover}
      onClick={() => {
        onSelect()
        if (!item.disabled) onExecute()
      }}
    >
      <SvgIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }}>
        <Icon />
      </SvgIcon>
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: 'common.white', fontWeight: 500 }}
          noWrap
        >
          {item.label}
        </Typography>
        {item.subtitle ? (
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.45)' }}
            noWrap
          >
            {item.subtitle}
          </Typography>
        ) : null}
      </Stack>
      {item.shortcut ? (
        <CommandBarKeycap label={formatCommandBarActionShortcut(item.shortcut)} />
      ) : null}
      {item.isRecent && recentLabel ? (
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}
        >
          {recentLabel}
        </Typography>
      ) : null}
    </Box>
  )
}
